import { Component, Prop, Element, Event, EventEmitter, h, Watch, State } from '@stencil/core';
import { focusElement } from '../../utils/functions';

/**
 * TODO
 */
@Component({
  tag: 'som-pagination',
  styleUrl: 'som-pagination.scss',
})
export class SomPagination {
  handlePreviousPageCb;

  handleNextPageCb;

  @Element() el: HTMLElement;


  /***
   * 
   * State Variables
   * 
   */
  @State() pageIndex = 0;
  @State() nbPages = 0; 

  /**
   * 
   * Properties
   * 
   */

  /**
   * This property determines the UI in which the pagination component will be rendered.  Use `grid` for add the element to `som-grid` and use `page` when adding it to non-grid use cases.
   */
  @Prop() view: 'grid' | 'page' = 'grid';

  /**
   * This property shows the number of items to display per page
   */
  @Prop() itemsPerPage: number = 10;

  @Watch('itemsPerPage')
  handleItemsPerPageChange() {
    this.calculateNumberOfPages();
  }

  /**
   * This property shows the total number of items in the grid
   */
  @Prop() totalItems: number;
  
  @Watch('totalItems')
  handleTotalItemsChange() {
    this.calculateNumberOfPages();
  }

  /**
   * This boolean adds borders to grid view.
   */
  @Prop() borders: boolean = true;

  /**
   * This property shows the current active page (starting index is `0`).
   */
  @Prop({ mutable: true, reflect: true }) page: number = 0;

  @Watch('page')
  handlePageChange(newValue) {
    this.pageIndex = newValue;
    //this.pageChanged.emit(newValue);
  }

  /**
   * This property describes the table associated with the pagination.  It is used to describe the pagination actions for screen readers.
   */
  @Prop() tableName: string = '';

  /**
   * 
   * EVENTS
   * 
   */

    /**
   * This event will be triggered anytime the page is changed.
   */
    @Event() pageChanged: EventEmitter; 

  
  /***
   * 
   * Lifecycle Methods
   * 
   */
  componentWillLoad() {
    this.calculateNumberOfPages();
    this.pageIndex = this.page;

    this.handlePreviousPageCb = this.handlePreviousPage.bind(this);
    this.handleNextPageCb = this.handleNextPage.bind(this);
  }

  /***
   * 
   * Component Methods
   * 
   */

  handlePageClick(page) {
    this.page = page;
    this.pageChanged.emit(this.page);
    this.updateActiveFocus();
  }

  handlePreviousPage() {
    if (this.page - 1 >= 0) {
      this.page = this.page - 1;
      this.pageChanged.emit(this.page);

      if (this.page === 0) {
        this.updateActiveFocus();
      }
    }
  }

  handleNextPage() {
    if (this.page + 1 < this.nbPages) {
      this.page = this.page + 1;
      this.pageChanged.emit(this.page);

      if (this.page === this.nbPages - 1) {
        this.updateActiveFocus();
      }
    }
  }

  updateActiveFocus() {
    // Wait after next frame render and focus new active button
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        focusElement(this.el.querySelector('li.active > som-button'));
      });
    });
  }

  calculateViewableItems() {
    const viewableEnd = this.itemsPerPage * (this.page + 1);
    const viewableStart = viewableEnd - (this.itemsPerPage - 1);

    if (this.totalItems !== undefined) {
      return ([
        <span class="sr-only">Showing {viewableStart} through {(viewableEnd < this.totalItems) ? viewableEnd : this.totalItems} records of {this.totalItems} total records</span>,
        <span aria-hidden="true">{viewableStart} - {(viewableEnd < this.totalItems) ? viewableEnd : this.totalItems} of {this.totalItems}</span>
      ])
    } else {
      return null;
    }
  }

  calculateNumberOfPages() {
    this.nbPages = Math.ceil(this.totalItems / this.itemsPerPage);
  }

  getPageElement(i: number) {
    return (
      <li key={`${i}-${this.pageIndex === i}`} class={this.pageIndex === i ? 'active' : ''}>
        <som-button aria-label={`${this.tableName} page ${i + 1}`} onClick={() => this.handlePageClick(i)} color={this.pageIndex === i ? 'primary' : 'white'} size="sm">
          {i + 1}
        </som-button>
      </li>
    );
  }

  getPageElements() {
    const pageElements = [];
    const LIMIT = 4;
    if (this.nbPages <= 7) {
      for (let i = 0; i < this.nbPages; i++) {
        pageElements.push(this.getPageElement(i));
      }
    } else if (this.pageIndex < LIMIT) {
      // Current page is in the beginning
      for (let i = 0; i < LIMIT + 1; i++) {
        pageElements.push(this.getPageElement(i));
      }

      // Add last page link
      pageElements.push(
        <li>
          <div class="three-dots" />
        </li>,
      );
      pageElements.push(this.getPageElement(this.nbPages - 1));
    } else if (this.pageIndex >= this.nbPages - LIMIT) {
      const startIndex = Math.max(0, this.nbPages - LIMIT - 1);

      pageElements.push(this.getPageElement(0));
      pageElements.push(
        <li>
          <div class="three-dots" />
        </li>,
      );

      // Current page is in the end
      for (let i = startIndex; i < this.nbPages; i++) {
        pageElements.push(this.getPageElement(i));
      }
    } else {
      // Add first page link
      pageElements.push(this.getPageElement(0));
      pageElements.push(
        <li>
          <div class="three-dots" />
        </li>,
      );

      for (let i = this.pageIndex - 1; i <= this.pageIndex + 1; i++) {
        pageElements.push(this.getPageElement(i));
      }

      // Add last page link
      pageElements.push(
        <li>
          <div class="three-dots" />
        </li>,
      );
      pageElements.push(this.getPageElement(this.nbPages - 1));
    }

    return pageElements;
  }

  render() {
    return (
      <div class={`pagination-container pagination-container__${this.view} ${(this.view === 'grid' && !this.borders) ? "no-border" : null}`}>
        <nav class="pagination" role="navigation" aria-label={`${this.tableName} pagination`}>
          <div class="pagination__previous">
            <som-button onClick={this.handlePreviousPageCb} disabled={this.pageIndex === 0} button-aria-label={`${this.tableName} previous page`} button-type="default" icon-position="only" icon="chevron-left" color="white" size="sm">
            </som-button>
          </div>
          <ul class="pagination__desktop">{this.getPageElements()}</ul>
          <div class="pagination__mobile">
            {this.pageIndex + 1} of {this.nbPages}
          </div>
          <div class="pagination__next">
            <som-button onClick={this.handleNextPageCb} disabled={this.pageIndex === this.nbPages - 1} button-aria-label={`${this.tableName} next page`} button-type="default" icon-position="only" icon="chevron-right" color="white" size="sm">
            </som-button>
          </div>
        </nav>
        <div class="pagination-items" aria-live="polite">
          {this.calculateViewableItems()} 
        </div>
      </div>
    );
  }
} 
